import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Modal from 'react/components/Modal';
import Button from 'react/components/Button';
import CircleLoader from 'react/components/CircleLoader';
import MaterialIcon from 'react/elements/MaterialIcon';
import NamePicker from 'react/components/search/NamePicker';
import MealIdeaPanel from './MealIdeaPanel';
import { convertMealName, fetchUnlinkedMealNames, loadMealIdeas, skipMealName } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex, selectMealIdeasLoaded, selectMealIdeasLoading } from '../../../redux/food/selectors';
import useOffline, { onlineOnly } from '../../hooks/useOffline';

const month = (ymd) => moment(ymd, 'YYYY-MM-DD').format('MMM YYYY');

/** "Sep 2019", or "Sep 2019 – Dec 2019" when the meals span months. */
export function dateRange(first, last) {
	return month(first) === month(last) ? month(first) : `${month(first)} – ${month(last)}`;
}

/**
 * The meal cleanup page: every name calendar meals carry with no meal idea behind them, most used
 * first, so a budget's years of meals typed or dropped on the calendar by name can be tied to meal
 * ideas a name at a time rather than a day at a time.
 *
 * Each name can be made a meal idea, or linked to the idea already called that; linked to some other
 * idea picked by name, for a variant spelling ("Daddy Time - Oatmeal" to Oatmeal); or skipped, for a
 * name that is a note rather than a meal ("Leftovers"). Every one of those takes the name off the
 * list. A name that is also a grocery item can keep that item as the idea's ingredient, which starts
 * unticked. All of it is meal_idea/convert_meal_name, as on the calendar and in the item editor.
 *
 * Making or linking an idea asks first, in a dialog that says what it will change, because nothing
 * here undoes it. Skipping does not ask: the skipped names are listed under the others, folded, each
 * with a way back.
 *
 * All of it is online-only, so offline every action is disabled, titled "Needs a connection".
 */
export default function MealCleanupTab() {
	const dispatch = useDispatch();
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const ideasLoaded = useSelector(selectMealIdeasLoaded);
	const ideasLoading = useSelector(selectMealIdeasLoading);
	const [names, setNames] = useState(null);
	const [skipped, setSkipped] = useState(null);
	const [showSkipped, setShowSkipped] = useState(false);
	const [busy, setBusy] = useState(null);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	const [openIdeaId, setOpenIdeaId] = useState(null);
	const [confirming, setConfirming] = useState(null);
	const mounted = useRef(true);
	const offline = useOffline();
	const ideas = useMemo(() => Object.values(ideasById), [ideasById]);
	const openIdea = openIdeaId ? ideasById[openIdeaId] || null : null;

	useEffect(() => {
		mounted.current = true;
		if(!ideasLoaded && !ideasLoading) {
			dispatch(loadMealIdeas());
		}
		fetchUnlinkedMealNames()
			.then((list) => mounted.current && setNames(list))
			.catch(() => mounted.current && setError('The meals did not load. Try again.'));
		return () => {
			mounted.current = false;
		};
	}, []);

	const loadSkipped = () => fetchUnlinkedMealNames(true)
		.then((list) => mounted.current && setSkipped(list))
		.catch(() => mounted.current && setError('The skipped names did not load. Try again.'));

	const toggleSkipped = () => {
		if(!showSkipped && skipped === null) {
			loadSkipped();
		}
		setShowSkipped(!showSkipped);
	};

	const without = (list, entry) => (list || []).filter((each) => each.name !== entry.name);

	const convert = (entry, options) => {
		setBusy(entry.name);
		setError(null);
		setNotice(null);
		return dispatch(convertMealName(null, entry.name, options)).then((result) => {
			if(!mounted.current) {
				return;
			}
			setBusy(null);
			if(!result) {
				setError(`${entry.name} could not be made a meal idea. Try again.`);
				return;
			}
			setNames((list) => without(list, entry));
			const meals = result.mealsLinked === 1 ? '1 meal' : `${result.mealsLinked} meals`;
			setNotice({ text: `${meals} called ${entry.name} now ${result.mealsLinked === 1 ? 'uses' : 'use'}`, ideaId: result.mealIdeaId });
		});
	};

	const skip = (entry, skip = true) => {
		setBusy(entry.name);
		setError(null);
		return skipMealName(entry.name, skip).then(() => {
			if(!mounted.current) {
				return;
			}
			setBusy(null);
			if(skip) {
				setNames((list) => without(list, entry));
				setSkipped((list) => (list === null ? null : [entry, ...list]));
			}
			else {
				setSkipped((list) => without(list, entry));
				setNames((list) => [...(list || []), entry].sort((a, b) => b.count - a.count));
			}
		}).catch(() => {
			if(mounted.current) {
				setBusy(null);
				setError(`${entry.name} could not be ${skip ? 'skipped' : 'brought back'}. Try again.`);
			}
		});
	};

	return (
		<div className='meal_cleanup'>
			{!!confirming &&
			<Modal className='meal_cleanup_confirm_modal' header={confirmHeader(confirming)} onClose={() => setConfirming(null)}>
				{(close) =>
				<ConvertConfirmation
					{...confirming}
					offline={offline}
					onCancel={close}
					onConfirm={() => {
						const { entry, idea, withIngredient } = confirming;
						close();
						convert(entry, { mealIdeaId: idea ? idea.id : null, withIngredient });
					}}
				/>
				}
			</Modal>
			}
			{!!openIdea &&
			<Modal className='meal_idea_modal' onClose={() => setOpenIdeaId(null)}>
				{(close) => <MealIdeaPanel key={openIdea.id} idea={openIdea} onClose={close} />}
			</Modal>
			}

			<p className='meal_cleanup__intro'>
				Meals on the calendar with no meal idea behind them, by name. Make each a meal idea, link it to
				one, or skip it if it is not a meal. Every meal with the name goes along, on every day.
			</p>
			{!!error && <p className='meal_cleanup__error red-text'>{error}</p>}
			{!!notice &&
			<p className='meal_cleanup__notice'>
				{notice.text}{' '}
				{ideasById[notice.ideaId] ?
				<button type='button' className='btn-flat meal_cleanup__open' onClick={() => setOpenIdeaId(notice.ideaId)}>
					{ideasById[notice.ideaId].name}
				</button> :
				'the meal idea'
				}.
			</p>
			}

			{names === null && !error && <CircleLoader />}
			{names !== null && names.length === 0 &&
			<p className='meal_cleanup__empty'>Every meal on the calendar has a meal idea.</p>
			}
			{names !== null && names.length > 0 &&
			<ul className='meal_cleanup__list'>
				{names.map((entry) =>
				<UnlinkedName
					key={entry.name}
					entry={entry}
					ideas={ideas}
					busy={busy === entry.name}
					offline={offline}
					onConvert={(idea, withIngredient) => setConfirming({ entry, idea, withIngredient })}
					onSkip={() => skip(entry)}
				/>
				)}
			</ul>
			}

			<button type='button' className='btn-flat meal_cleanup__skipped-toggle' aria-expanded={showSkipped} onClick={toggleSkipped}>
				<MaterialIcon>{showSkipped ? 'expand_less' : 'expand_more'}</MaterialIcon>
				Skipped names
			</button>
			{showSkipped && skipped === null && <CircleLoader />}
			{showSkipped && skipped !== null && skipped.length === 0 &&
			<p className='meal_cleanup__empty'>Nothing skipped.</p>
			}
			{showSkipped && skipped !== null && skipped.length > 0 &&
			<ul className='meal_cleanup__list is-skipped'>
				{skipped.map((entry) =>
				<li key={entry.name} className='meal_cleanup__name'>
					<div className='meal_cleanup__what'>
						<strong>{entry.name}</strong>
						<span className='meal_cleanup__muted'>{entry.count === 1 ? '1 meal' : `${entry.count} meals`} · {dateRange(entry.first, entry.last)}</span>
					</div>
					<button type='button' className='btn-flat meal_cleanup__unskip' disabled={busy === entry.name} {...onlineOnly(offline)} onClick={() => skip(entry, false)}>
						Bring back
					</button>
				</li>
				)}
			</ul>
			}
		</div>
	);
}

/** One name and what can be done with it. */
function UnlinkedName({ entry, ideas, busy, offline = false, onConvert, onSkip }) {
	const [withIngredient, setWithIngredient] = useState(false);
	const [picking, setPicking] = useState(false);
	const same = entry.meal_idea_id ? ideas.find((idea) => Number(idea.id) === Number(entry.meal_idea_id)) || null : null;
	const isItem = !!entry.grocery_item_id;

	return (
		<li className='meal_cleanup__name'>
			<div className='meal_cleanup__what'>
				<strong>{entry.name}</strong>
				<span className='meal_cleanup__muted'>
					{entry.count === 1 ? '1 meal' : `${entry.count} meals`} · {dateRange(entry.first, entry.last)}
					{isItem && ' · also a grocery item'}
				</span>
			</div>
			<div className='meal_cleanup__actions'>
				<button type='button' className='btn-flat meal_cleanup__make' disabled={busy} {...onlineOnly(offline)} onClick={() => onConvert(same, isItem && withIngredient)}>
					<MaterialIcon>lightbulb</MaterialIcon>
					{same ? <>Link to &ldquo;{same.name}&rdquo;</> : 'Make a meal idea'}
				</button>
				<button type='button' className='btn-flat meal_cleanup__link' disabled={busy} {...onlineOnly(offline)} aria-expanded={picking} onClick={() => setPicking(!picking)}>
					<MaterialIcon>link</MaterialIcon>
					Link to another
				</button>
				<button type='button' className='btn-flat meal_cleanup__skip' disabled={busy} {...onlineOnly(offline)} onClick={onSkip}>
					<MaterialIcon>visibility_off</MaterialIcon>
					Skip
				</button>
			</div>
			{isItem &&
			<label className='meal_cleanup__ingredient'>
				<input
					type='checkbox'
					className='filled-in'
					checked={withIngredient}
					onChange={(event) => setWithIngredient(event.target.checked)}
				/>
				<span>Keep the grocery item {entry.name} as its ingredient</span>
			</label>
			}
			{picking &&
			<div className='meal_cleanup__picker'>
				<NamePicker
					items={ideas}
					placeholder='Search meal ideas'
					busy={busy}
					listAllWhenBlank={false}
					emptyText='Type the meal idea it should be.'
					onPick={(idea) => {
						setPicking(false);
						onConvert(idea, isItem && withIngredient);
					}}
				/>
			</div>
			}
		</li>
	);
}

function confirmHeader({ entry, idea }) {
	return idea ? `Link ${entry.name} to ${idea.name}?` : `Make ${entry.name} a meal idea?`;
}

/**
 * What converting a name will do, spelled out before it is done: Meal_Name_Converter's effects, in
 * the terms of this one name. `idea` is the idea it links to, or null for one made new or found by
 * the name.
 */
export function ConvertConfirmation({ entry, idea, withIngredient, offline = false, onCancel, onConfirm }) {
	const meals = entry.count === 1 ? 'The 1 meal' : `All ${entry.count} meals`;
	const ideaName = idea ? idea.name : entry.name;
	const renamed = idea && idea.name.trim().toLowerCase() !== entry.name.trim().toLowerCase();

	return (
		<div className='meal_cleanup_confirm'>
			<ul className='meal_cleanup_confirm__effects'>
				{!idea &&
				<li>A meal idea called <strong>{entry.name}</strong> is made, if there is not one already.</li>
				}
				<li>
					{meals} on the calendar called <strong>{entry.name}</strong> ({dateRange(entry.first, entry.last)})
					{' '}{entry.count === 1 ? 'becomes' : 'become'} <strong>{ideaName}</strong>, with its ingredients and recipe.
					{renamed && <> {entry.count === 1 ? 'It keeps' : 'They keep'} the name {entry.name} on the calendar.</>}
				</li>
				{!!entry.grocery_item_id &&
				<li>Bought grocery lists that had <strong>{entry.name}</strong> on them will show it was for {ideaName}.</li>
				}
				{withIngredient &&
				<li>{ideaName} gets the grocery item <strong>{entry.name}</strong> as an ingredient.</li>
				}
				<li>{entry.name} comes off this list.</li>
			</ul>
			<p className='meal_cleanup_confirm__warning'>This can&rsquo;t be undone from here.</p>
			<div className='meal_cleanup_confirm__buttons'>
				<Button flat type='button' className='meal_cleanup_confirm__cancel' onClick={onCancel}>Cancel</Button>
				<Button flat type='button' className='meal_cleanup_confirm__go' data-autofocus {...onlineOnly(offline)} onClick={onConfirm}>
					{idea ? `Link to ${idea.name}` : 'Make the meal idea'}
				</Button>
			</div>
		</div>
	);
}
